/**
 * Native (Capacitor) Google sign-in — Android account picker → Google ID token.
 *
 * T27.2.2.1: Google blocks the web GIS flow inside embedded WebViews
 * (`disallowed_useragent`). On a native platform we instead drive the OS
 * Google account picker via `@capgo/capacitor-social-login` (Android
 * Credential Manager / Sign in with Google) and feed the resulting **ID
 * token** into the *same* `POST /api/auth/google` the web path uses.
 *
 * Why the audience still matches the backend: the plugin is initialised with
 * `google.webClientId = <the web OAuth client ID>` (the serverClientId). The
 * returned ID token's `aud` is exactly that web client ID, so the existing
 * `verifyIdToken({ audience: GOOGLE_CLIENT_ID })` accepts it unchanged
 * ([backend/src/routes/auth.ts] line ~136). The Android OAuth client
 * (package + SHA-1) only authorizes the app to *request* sign-in; it does not
 * become the token audience.
 *
 * Portability: every `@capacitor/*` / plugin import lives here in `apps/web`
 * and is reached only after `isNativePlatform()` is true, so this module is
 * inert on web. Nothing Capacitor-shaped leaks into `packages/shared`
 * (CP1–CP4 stay green). The plugin is dynamically imported so the web bundle
 * never eagerly loads the native code path.
 */

import { Capacitor } from '@capacitor/core';
import type { AnalyticsPlatform } from '../analytics';

/** True when running inside the native (Capacitor) shell, false on web. */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * The concrete platform — `android` / `ios` / `web` — from
 * `Capacitor.getPlatform()`, NOT the boolean `isNativePlatform()`. Used as the
 * single source for the observability `platform` value so PostHog's
 * super-property and Sentry's tag agree, and so the two native platforms are
 * distinguishable (not collapsed to a single `android`). `getPlatform()` is
 * always one of those three strings, so the cast is total.
 */
export function resolvePlatform(): AnalyticsPlatform {
  return Capacitor.getPlatform() as AnalyticsPlatform;
}

let initialized = false;

/**
 * Obtain a Google ID token via the native account picker.
 *
 * @param webClientId the *web* OAuth client ID (same value the web GIS path
 *        uses, i.e. `VITE_GOOGLE_CLIENT_ID`). Wired as the plugin's
 *        `webClientId` / serverClientId so the token's `aud` matches the
 *        backend audience.
 * @returns the Google ID token, suitable as the `credential` for
 *          `POST /api/auth/google`.
 * @throws if the user cancels, the picker errors, or no ID token comes back.
 */
export async function signInWithGoogleNative(webClientId: string): Promise<string> {
  // Dynamic import keeps the plugin out of the eager web bundle; it is only
  // pulled in once we are on a native platform and the user taps sign-in.
  const { SocialLogin } = await import('@capgo/capacitor-social-login');

  if (!initialized) {
    await SocialLogin.initialize({
      google: {
        // Android + Web both read webClientId; this is the serverClientId
        // source that fixes the returned ID token's audience.
        webClientId,
      },
    });
    initialized = true;
  }

  const { result } = await SocialLogin.login({
    provider: 'google',
    options: {},
  });

  // Online mode (default) returns an idToken; offline mode returns only a
  // serverAuthCode. We use online mode, so an absent idToken is an error.
  if (result.responseType !== 'online' || !result.idToken) {
    throw new Error('Google sign-in did not return an ID token.');
  }

  return result.idToken;
}
